import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

/**
 *  ADMIN › IDŐBÉLYEG-FELÜLHITELESÍTÉS – a digitális bizonyítványok 80 éves megőrzésének felügyelete
 *  (backend/idobelyeg-megujitas.js, ltv-adatok.js).
 *
 *  Nkt. 57. § (6a): a digitális bizonyítvány az igazgató aláírásával és időbélyeggel ellátott
 *  elektronikus okirat, amelyet 80 évig kell őrizni. A rendszer naponta megújítja az időbélyeget
 *  (a TSA-tanúsítvány lejárata előtt, legkésőbb 5 évente), és előtte a tanúsítványláncot és a
 *  visszavonási adatokat (LTV/DSS) is a fájlba teszi. Itt látszik, hány dokumentum van felügyelet
 *  alatt, mikor a legközelebbi esedékesség, és mely tételeknél volt hiba; a „Most futtasd” egy kört
 *  azonnal elindít. Hiba esetén a rendszer üzenetet is küld az általános adminisztrátoroknak.
 *  Felügyelt típusok: bizonyítvány, pótlap, törzslap és (2026-10-16) a kötelező nyomtatványok –
 *  típusonkénti kártyával és a listák típusszűrőjével.
 */
@Component({
  selector: 'app-admin-idobelyeg-felugyelet',
  standalone: true,
  imports: [CommonModule, IonicModule],
  templateUrl: './idobelyeg-felugyelet.component.html',
  styleUrls: ['./idobelyeg-felugyelet.component.scss'],
})
export class AdminIdobelyegFelugyeletComponent implements OnInit {
  betolt = false;
  fut = false;
  intezmeny = '';
  a: any = null;
  utolsoFutas: any = null;

  readonly fajtaNev: Record<string, string> = {
    hiba: 'Hiba',
    'tsa-lejart': 'Lejárt TSA-tanúsítvány',
    'lejart-esedekesseg': 'Elmaradt felülhitelesítés',
  };

  /*  A felügyelt dokumentumtípusok (a kiszolgáló `tipusok` listája adja a számokat; ez a
   *  tartalék név, ha egy régebbi kiszolgáló még nem küldi). 2026-10-16: a kötelező
   *  nyomtatványok (20/2012. EMMI r. 94. §) is felülhitelesítés alatt vannak. */
  readonly tipusNev: Record<string, string> = {
    bizonyitvany: 'Bizonyítvány',
    potlap: 'Bizonyítvány-pótlap',
    torzslap: 'Törzslap',
    nyomtatvany: 'Kötelező nyomtatvány',
    bizonyitvany_masodlat: 'Bizonyítványmásodlat',
  };

  /** A hiba- és LTV-lista szűrője: '' = minden típus. */
  tipusSzuro = '';

  constructor(private http: HttpClient, private data: DataService) {}

  get tipusok(): { tipus: string; nev: string; osszes: number; hibas: number; esedekes: number; ltv_hianyos: number }[] {
    return Array.isArray(this.a?.tipusok) ? this.a.tipusok : [];
  }

  tipusCimke(t: string | null | undefined): string {
    const k = String(t || '');
    return this.tipusNev[k] || this.tipusok.find((x) => x.tipus === k)?.nev || k;
  }

  get szurtGondok(): any[] {
    const l: any[] = this.a?.gondok || [];
    return this.tipusSzuro ? l.filter((g) => g?.tipus === this.tipusSzuro) : l;
  }

  get szurtLtv(): any[] {
    const l: any[] = this.a?.ltv_hianyos || [];
    return this.tipusSzuro ? l.filter((g) => g?.tipus === this.tipusSzuro) : l;
  }

  tipusValaszt(t: any) {
    this.tipusSzuro = String(t ?? '');
  }

  async ngOnInit() {
    const sajat: any = await this.data.getFromStorage('sajatIntezmeny').catch(() => null);
    this.intezmeny = String(sajat?.intezmenyId || '');
    await this.toltes();
  }

  get gondDb(): number { return this.a?.gondok?.length || 0; }

  async toltes() {
    if (!this.intezmeny) return;
    this.betolt = true;
    try {
      this.a = await firstValueFrom(this.http.get('/api/idobelyeg/allapot', { params: { intezmeny: this.intezmeny } }));
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült betölteni a felülhitelesítés állapotát.', 'error');
    } finally { this.betolt = false; }
  }

  async futtat() {
    this.fut = true;
    try {
      const r: any = await firstValueFrom(this.http.post('/api/idobelyeg/futtat', { intezmeny: this.intezmeny }));
      this.utolsoFutas = r?.kor || null;
      if (r?.allapot) this.a = r.allapot;
      const k = r?.kor || {};
      const uzenet = `${k.ellenorizve || 0} dokumentum ellenőrizve, ${k.megujitva || 0} felülhitelesítve, ${k.ltv_potolva || 0} LTV-pótlás, ${k.hiba || 0} hiba.`;
      this.data.sendToast(uzenet + (r?.riasztas?.kuldve ? ' Az adminisztrátorok értesítést kaptak.' : ''), k.hiba ? 'warning' : 'success');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült elindítani.', 'error');
    } finally { this.fut = false; }
  }

  async ujraprobal(g: any) {
    try {
      await firstValueFrom(this.http.post(`/api/idobelyeg/ujraprobal/${encodeURIComponent(g.dokumentum_id)}`, { intezmeny: this.intezmeny }));
      this.data.sendToast('A tétel a következő körben újra sorra kerül – a „Most futtasd” azonnal elindítja.', 'success');
      await this.toltes();
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'Nem sikerült.', 'error'); }
  }
}
